// src/features/ask/components/AskTurnCard.tsx
//
// One question and its answer inside the «Preguntar» side panel: the question
// as the person's bubble, then the answer, the notice of any change made to
// the open screen (with «Deshacer»), the tables the API built from the
// queries it ran (as cards — the panel is narrow on every screen) and
// «Cómo se ha calculado». A pending turn shows progress; a failed one, the
// reason and a retry.
import React from 'react';
import { Alert, Box, Button, Chip, Collapse, LinearProgress, Stack, Typography } from '@mui/material';
import ExpandMoreRounded from '@mui/icons-material/ExpandMoreRounded';
import TuneRounded from '@mui/icons-material/TuneRounded';
import { useTranslate } from 'react-admin';
import { ResponsiveMetricsTable, type MetricsColumn } from '@/components/common/ResponsiveMetricsTable';
import { formatAskCell, formatAskDate } from '@/lib/askFormat';
import type { AskResponse, AskTable } from '@/types/assistant';
import type { AskTurn } from '../AssistantProvider';

const AskResultTable: React.FC<{ table: AskTable }> = ({ table }) => {
  const translate = useTranslate();
  const columns: MetricsColumn<Record<string, unknown>>[] = table.columns.map((column, index) => ({
    key: column.key,
    label: column.label,
    align: index === 0 || column.unit === 'text' ? 'left' : 'right',
    render: (row) => formatAskCell(row[column.key], column.unit, translate),
  }));

  return (
    <Box>
      <Typography variant="subtitle2" component="h3" sx={{ mb: 1 }}>
        {table.title}
      </Typography>
      <ResponsiveMetricsTable
        columns={columns}
        rows={table.rows}
        getRowKey={(row) => String(row.hotel_id ?? row.property_id ?? row.indicator ?? JSON.stringify(row))}
        forceCards
      />
      {table.note && (
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 0.75 }}>
          {table.note}
        </Typography>
      )}
    </Box>
  );
};

const AskSources: React.FC<{ response: AskResponse }> = ({ response }) => {
  const translate = useTranslate();
  const [open, setOpen] = React.useState(false);
  if (response.sources.length === 0) return null;

  return (
    <Box>
      <Button
        size="small"
        color="inherit"
        onClick={() => setOpen((value) => !value)}
        aria-expanded={open}
        endIcon={<ExpandMoreRounded sx={{ transform: open ? 'rotate(180deg)' : 'none', transition: 'transform 150ms' }} />}
        sx={{ px: 0, color: 'text.secondary' }}
      >
        {translate('resources.ask.answer.howCalculated')}
      </Button>
      <Collapse in={open}>
        <Stack component="ul" spacing={1} sx={{ listStyle: 'none', p: 0, m: 0, mt: 1 }}>
          {response.sources.map((source, index) => (
            <Box component="li" key={index}>
              <Chip
                size="small"
                label={translate(source.origin === 'pms' ? 'resources.ask.answer.originPms' : 'resources.ask.answer.originRoommatik')}
                sx={{ mb: 0.5 }}
              />
              <Typography variant="body2" color="text.secondary">
                {translate('resources.ask.answer.source', {
                  description: source.description,
                  from: formatAskDate(source.from),
                  to: formatAskDate(source.to),
                  smart_count: source.hotelCount,
                })}
              </Typography>
            </Box>
          ))}
        </Stack>
        <Typography variant="caption" color="text.secondary" component="p" sx={{ mt: 1.5 }}>
          {translate('resources.ask.answer.disclaimer')}
        </Typography>
      </Collapse>
    </Box>
  );
};

export interface AskTurnCardProps {
  turn: AskTurn;
  onRetry: (turn: AskTurn) => void;
  onUndo: (turnId: number) => void;
  canUndo: boolean;
}

export const AskTurnCard: React.FC<AskTurnCardProps> = ({ turn, onRetry, onUndo, canUndo }) => {
  const translate = useTranslate();
  const { state } = turn;

  return (
    <Box component="article" aria-label={turn.question}>
      <Box
        sx={{
          ml: 'auto',
          mb: 1.5,
          maxWidth: '85%',
          width: 'fit-content',
          px: 1.5,
          py: 1,
          borderRadius: 2,
          bgcolor: 'action.hover',
        }}
      >
        <Typography variant="body2" component="h3" sx={{ fontWeight: 600, whiteSpace: 'pre-line' }}>
          {turn.question}
        </Typography>
      </Box>

      {state.status === 'pending' && (
        <Box role="status" aria-live="polite">
          <LinearProgress sx={{ mb: 1 }} />
          <Typography variant="body2" color="text.secondary">
            {translate('resources.ask.answer.thinking')}
          </Typography>
        </Box>
      )}

      {state.status === 'error' && (
        <Alert
          severity="error"
          variant="outlined"
          action={
            <Button color="inherit" size="small" onClick={() => onRetry(turn)}>
              {translate('common.actions.retry')}
            </Button>
          }
        >
          {translate(state.messageKey)}
        </Alert>
      )}

      {state.status === 'done' && (
        <Stack spacing={2}>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
            {state.response.answer}
          </Typography>
          {state.applied && state.response.viewAction && (
            <Alert
              severity="info"
              variant="outlined"
              icon={<TuneRounded fontSize="small" />}
              action={
                canUndo && !state.applied.undone ? (
                  <Button color="inherit" size="small" onClick={() => onUndo(turn.id)}>
                    {translate('resources.ask.view.undo')}
                  </Button>
                ) : undefined
              }
            >
              {translate(state.applied.undone ? 'resources.ask.view.undone' : 'resources.ask.view.applied', {
                description: state.response.viewAction.description,
              })}
            </Alert>
          )}
          {state.response.incomplete && <Alert severity="info">{translate('resources.ask.answer.incomplete')}</Alert>}
          {state.response.tables.map((table, index) => (
            <AskResultTable key={index} table={table} />
          ))}
          <AskSources response={state.response} />
        </Stack>
      )}
    </Box>
  );
};

export default AskTurnCard;
